import { catchError, mergeMap, startWith, map, concatMap } from 'rxjs/operators';
import { from, concat, of } from 'rxjs';
import { ofType } from 'redux-observable';

import { setLoader, setModal, handleUnknownError, setUser, clearUser, createToast, forgotPasswordUserData } from './deps';
import {
  LOGIN_REQUEST,
  VERIFY_DEVICE_OTP_REQUEST,
  RESEND_OTP_REQUEST,
  LOGOUT_REQUEST,
  REFRESH_TOKEN_REQUEST,
  REGISTER_REQUEST,
  RESET_PASSWORD_REQUEST,
  ACTIVATE_REQUEST,
  FORGOT_PASSWORD_REQUEST,
  RESEND_ACTIVATION_EMAIL
} from './types';

import { registerSuccess, logoutRequest } from './actions';

export function login($action, $state, { api }) {
  const $api = api.getModuleByName('auth');

  return $action.pipe(
    ofType(LOGIN_REQUEST),
    mergeMap(({ payload: { data, notifyForm, otpCallbacks } }) =>
      // otp-flow logins defer token persistence so a late response can be dropped
      from($api.login(data, otpCallbacks ? { deferCompletion: true } : undefined)).pipe(
        concatMap(result => {
          notifyForm();

          if (result && result.otpRequired) {
            if (otpCallbacks && otpCallbacks.onOtpRequired) {
              otpCallbacks.onOtpRequired(result.verificationTokenHash);
            }
            if (otpCallbacks && otpCallbacks.onSettled) otpCallbacks.onSettled();

            return [setLoader(false)];
          }

          if (result && result.deferredLogin) {
            // full login success on an otp-flow request (device became trusted server-side):
            // apply it only while the flow is still active, otherwise drop it entirely
            if (otpCallbacks.shouldApply && !otpCallbacks.shouldApply()) {
              if (otpCallbacks.onSettled) otpCallbacks.onSettled();

              return [setLoader(false)];
            }

            const user = $api.completeLogin(result.response);

            if (otpCallbacks.onFullLogin) otpCallbacks.onFullLogin();
            if (otpCallbacks.onSettled) otpCallbacks.onSettled();

            return [setUser(user), setLoader(false)];
          }

          return [setUser(result), setLoader(false)];
        }),

        // callbacks must fire on every settle path, whatever the error type
        catchError(err => {
          if (otpCallbacks && otpCallbacks.onError) {
            otpCallbacks.onError(err);
          }
          if (otpCallbacks && otpCallbacks.onSettled) otpCallbacks.onSettled();

          // unknown errors -> global error modal actions; known ones rethrow into the inner catch
          return handleUnknownError(err).pipe(
            catchError(knownErr => {
              notifyForm({ message: knownErr.message });

              return of(setLoader(false));
            })
          );
        }),

        startWith(setLoader(true))
      )
    )
  );
}

export function verifyDeviceOtp($action, $state, { api }) {
  const $api = api.getModuleByName('auth');

  return $action.pipe(
    ofType(VERIFY_DEVICE_OTP_REQUEST),
    mergeMap(({ payload: { data, callbacks = {} } }) =>
      from($api.verifyDeviceOtp(data)).pipe(
        concatMap(response => {
          // tokens are persisted only here, and only for the still-active flow
          if (callbacks.shouldApply && !callbacks.shouldApply()) {
            if (callbacks.onSettled) callbacks.onSettled();

            return [setLoader(false)];
          }

          const user = $api.completeLogin(response);

          if (callbacks.onSuccess) callbacks.onSuccess();
          if (callbacks.onSettled) callbacks.onSettled();

          return [setUser(user), setLoader(false)];
        }),

        // callbacks must fire on every settle path, whatever the error type
        catchError(err => {
          if (callbacks.onError && (!callbacks.shouldApply || callbacks.shouldApply())) {
            callbacks.onError(err);
          }
          if (callbacks.onSettled) callbacks.onSettled();

          return handleUnknownError(err).pipe(
            catchError(() => of(setLoader(false)))
          );
        }),

        startWith(setLoader(true))
      )
    )
  );
}

export function resendOtp($action, $state, { api }) {
  const $api = api.getModuleByName('auth');

  return $action.pipe(
    ofType(RESEND_OTP_REQUEST),
    mergeMap(({ payload: { verificationTokenHash, callbacks = {} } }) =>
      from($api.resendOtp(verificationTokenHash)).pipe(
        concatMap(response => {
          if (!callbacks.shouldApply || callbacks.shouldApply()) {
            if (callbacks.onSuccess) callbacks.onSuccess(response);
          }
          if (callbacks.onSettled) callbacks.onSettled();

          return [setLoader(false)];
        }),

        // callbacks must fire on every settle path, whatever the error type
        catchError(err => {
          if (callbacks.onError && (!callbacks.shouldApply || callbacks.shouldApply())) {
            callbacks.onError(err);
          }
          if (callbacks.onSettled) callbacks.onSettled();

          return handleUnknownError(err).pipe(
            catchError(() => of(setLoader(false)))
          );
        }),

        startWith(setLoader(true))
      )
    )
  );
}

export function logout($action, $state, { api }) {
  const $api = api.getModuleByName('auth');

  return $action.pipe(
    ofType(LOGOUT_REQUEST),
    mergeMap(() => from($api.logout()).pipe(map(() => clearUser())))
  );
}

export function register($action, $state, { api }) {
  const $api = api.getModuleByName('auth');

  return $action.pipe(
    ofType(REGISTER_REQUEST),
    mergeMap(({ payload: { data, notifyForm } }) =>
      from($api.register(data)).pipe(
        concatMap((response) => {
          notifyForm();
          // response added to show error of xXtra Card error msg
          return [registerSuccess(data), setUser(response), setLoader(false), setModal('accountCreated', true)];
        }),

        catchError(handleUnknownError),
        catchError(err => {
          const actions = [setLoader(false)];

          if (err.errors) {
            notifyForm(err.errors);
          } else {
            actions.push(createToast('warning', err.message.replace(':', '')));
            notifyForm({ message: err.message });
          }

          return concat(actions);
        }),

        startWith(setLoader(true))
      )
    )
  );
}

export function refreshToken($action, $state, { api }) {
  const $api = api.getModuleByName('auth');

  return $action.pipe(
    ofType(REFRESH_TOKEN_REQUEST),
    mergeMap(({ payload }) =>
      from($api.updateToken()).pipe(
        mergeMap(user => {
          const actions = [setUser(user), setLoader(false)];

          if (payload.type) {
            actions.push(payload);
          }

          return concat(actions);
        }),

        catchError(handleUnknownError),
        catchError(() => concat([logoutRequest(), setLoader(false)])),

        startWith(setLoader(true))
      )
    )
  );
}

export function activate($action, $state, { api }) {
  const $api = api.getModuleByName('auth');

  return $action.pipe(
    ofType(ACTIVATE_REQUEST),
    mergeMap(({ payload: { token, memberId } }) =>
      from($api.activate(token, memberId)).pipe(
        concatMap(() => [createToast('info', 'toasters.user_activation'), setLoader(false)]),

        catchError(handleUnknownError),
        catchError(() =>
          of(createToast('warning', 'toasters.user_activation_failed'), setLoader(false))
        ),

        startWith(setLoader(true))
      )
    )
  );
}

export function resendActivationEmail($action, $state, { api }) {
  const $api = api.getModuleByName('auth');
  return $action.pipe(
    ofType(RESEND_ACTIVATION_EMAIL),
    mergeMap(({ payload: { email } }) =>
      from($api.resendActivationEmail(email)).pipe(
        concatMap(() => [
          setLoader(false)]),
        catchError(handleUnknownError),
        catchError(() =>
          of(
            setLoader(false)
          )
        ),
        startWith(setLoader(true))
      )
    )
  );
}

export function forgotPassword($action, $state, { api }) {
  const $api = api.getModuleByName('auth');

  return $action.pipe(
    ofType(FORGOT_PASSWORD_REQUEST),
    mergeMap(({ payload: { data, notifyForm } }) =>
      from($api.forgotPassword(data)).pipe(
        concatMap((user) => {
          notifyForm();

          return [
            forgotPasswordUserData(user),
            setModal('forgotPassword', false),
            // setModal('forgotPasswordSuccess', true),
            setLoader(false)
          ];
        }),

        catchError(handleUnknownError),
        catchError(err => {
          const actions = [setLoader(false)];

          if (err.errors) {
            notifyForm(err.errors);
          } else {
            actions.push(createToast('warning', err.message));
            notifyForm();
          }

          return concat(actions);
        }),

        startWith(setLoader(true))
      )
    )
  );
}

export function resetPassword($action, $state, { api }) {
  const $api = api.getModuleByName('auth');

  return $action.pipe(
    ofType(RESET_PASSWORD_REQUEST),
    mergeMap(({ payload: { data, redirect, notifyForm } }) =>
      from($api.resetPassword(data)).pipe(
        concatMap(() => {
          notifyForm();
          redirect();

          return [createToast('info', 'toasters.password_reset'), setLoader(false)];
        }),

        catchError(handleUnknownError),
        catchError(err => {
          notifyForm({ message: err.message });

          return of(setLoader(false));
        }),

        startWith(setLoader(true))
      )
    )
  );
}
